/**
 * Milele Garden — motore di movimento.
 *
 * Gestisce, in modo passivo e degradabile:
 *   - rivelazioni allo scroll ([data-garden-reveal])
 *   - parallasse leggera ([data-garden-parallax])
 *   - inclinazione delle card ([data-garden-tilt])
 *   - bottoni magnetici ([data-garden-magnetic])
 *   - contatori animati ([data-garden-count])
 *   - particelle ambientali (.garden-particles[data-count])
 *
 * Tutto viene disattivato con `prefers-reduced-motion: reduce` o su dispositivi
 * a bassa potenza, coerentemente con assets/view-transitions.js del tema.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @returns {boolean} */
function isLowPower() {
  /* eslint-disable-next-line compat/compat */
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/** @returns {boolean} */
function motionAllowed() {
  return !REDUCED_MOTION.matches;
}

/* ------------------------------------------------------------------ *
 * Rivelazioni allo scroll
 * ------------------------------------------------------------------ */

/**
 * Elementi nascosti in attesa di entrare in pagina.
 *
 * Non usiamo IntersectionObserver: se l'utente salta un elemento (scroll rapido,
 * ancora, ripristino della posizione al ritorno indietro) passando da "sotto il
 * viewport" a "sopra il viewport" senza stati intermedi, l'observer non emette
 * alcuna callback e l'elemento resterebbe invisibile per sempre. Un controllo su
 * scroll con rAF non ha questo buco: rivela tutto cio' che ha superato la soglia,
 * da qualunque direzione.
 *
 * @type {Set<HTMLElement>}
 */
const armati = new Set();

/**
 * Contatori in attesa di entrare in pagina, con la funzione che li avvia.
 * @type {Map<HTMLElement, (element: HTMLElement) => void>}
 */
const contatoriInAttesa = new Map();

let revealTicking = false;

/** Frazione dell'altezza schermo oltre la quale un elemento e' considerato "entrato". */
const SOGLIA_RIVELAZIONE = 0.92;

function checkReveals() {
  revealTicking = false;
  if (!armati.size && !contatoriInAttesa.size) return;

  const viewport = window.innerHeight || document.documentElement.clientHeight;
  const linea = viewport * SOGLIA_RIVELAZIONE;

  for (const element of armati) {
    if (!element.isConnected) {
      armati.delete(element);
      continue;
    }
    if (element.getBoundingClientRect().top < linea) {
      element.classList.add('garden-revealed');
      armati.delete(element);
    }
  }

  for (const [element, run] of contatoriInAttesa) {
    if (!element.isConnected) {
      contatoriInAttesa.delete(element);
      continue;
    }
    if (element.getBoundingClientRect().top < viewport * 0.85) {
      delete element.dataset.gardenCountPending;
      element.dataset.gardenCountDone = 'true';
      contatoriInAttesa.delete(element);
      run(element);
    }
  }
}

function requestReveals() {
  if (revealTicking) return;
  revealTicking = true;
  requestAnimationFrame(checkReveals);
}

if (!window.__gardenRevealBound) {
  window.__gardenRevealBound = true;
  // Horizon su desktop fa scorrere `.page-wrapper` (html e body sono overflow:
  // hidden), su mobile la finestra: l'ascolto in fase di cattura sul documento
  // intercetta lo scroll di QUALUNQUE contenitore, presente o futuro.
  document.addEventListener('scroll', requestReveals, { capture: true, passive: true });
  window.addEventListener('resize', requestReveals, { passive: true });
  // Il ripristino della posizione di scroll puo' avvenire dopo DOMContentLoaded.
  window.addEventListener('load', requestReveals);
  window.addEventListener('pageshow', requestReveals);
  // Scheda aperta in background: al ritorno in primo piano completa le rivelazioni in sospeso.
  document.addEventListener('visibilitychange', requestReveals);
}

/**
 * Registra tutti gli elementi rivelabili dentro `root`.
 *
 * Il CSS nasconde solo gli elementi con [data-garden-armed], attributo che
 * mettiamo qui: se questo file non venisse mai eseguito, nulla resterebbe
 * invisibile. Gli elementi gia' visibili a schermo vengono rivelati subito,
 * senza armarli, cosi' non c'e' nessun lampo sopra la piega.
 *
 * @param {ParentNode} root
 */
function initReveals(root) {
  const elements = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-garden-reveal]')]);
  if (!elements.length) return;

  if (!motionAllowed()) {
    elements.forEach((element) => element.classList.add('garden-revealed'));
    return;
  }

  const viewport = window.innerHeight || document.documentElement.clientHeight;
  const linea = viewport * SOGLIA_RIVELAZIONE;

  for (const element of elements) {
    if (element.classList.contains('garden-revealed') || element.hasAttribute('data-garden-armed')) continue;

    // Gia' entrato in pagina: mostralo subito, senza armarlo. Cosi' il contenuto
    // sopra la piega non lampeggia mai da invisibile a visibile.
    if (element.getBoundingClientRect().top < linea) {
      element.classList.add('garden-revealed');
      continue;
    }

    // Scaglionamento: [data-garden-stagger] su un contenitore indicizza i figli rivelabili.
    const group = element.closest('[data-garden-stagger]');
    if (group instanceof HTMLElement && !element.style.getPropertyValue('--garden-reveal-index')) {
      const siblings = [...group.querySelectorAll('[data-garden-reveal]')].filter(
        (candidate) => candidate.closest('[data-garden-stagger]') === group
      );
      const index = siblings.indexOf(element);
      if (index > 0) element.style.setProperty('--garden-reveal-index', String(Math.min(index, 8)));
    }

    element.setAttribute('data-garden-armed', '');
    armati.add(element);
  }

  requestReveals();
}

/* ------------------------------------------------------------------ *
 * Parallasse
 * ------------------------------------------------------------------ */

/** @type {{ element: HTMLElement, speed: number }[]} */
const parallaxItems = [];
let parallaxTicking = false;

function renderParallax() {
  parallaxTicking = false;
  const viewport = window.innerHeight;

  // Nell'editor tema le sezioni vengono sostituite: scarta i nodi non piu' in pagina.
  for (let i = parallaxItems.length - 1; i >= 0; i -= 1) {
    if (!parallaxItems[i].element.isConnected) parallaxItems.splice(i, 1);
  }

  for (const { element, speed } of parallaxItems) {
    const rect = element.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > viewport + 200) continue;

    // -1 quando l'elemento e' appena sotto il viewport, +1 quando e' appena sopra.
    const progress = (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2);
    element.style.setProperty('--garden-parallax-y', `${(progress * speed * 100).toFixed(2)}px`);
    element.style.transform = `translate3d(0, ${(progress * speed * 100).toFixed(2)}px, 0)`;
  }
}

function requestParallax() {
  if (parallaxTicking) return;
  parallaxTicking = true;
  requestAnimationFrame(renderParallax);
}

/** @param {ParentNode} root */
function initParallax(root) {
  if (!motionAllowed() || isLowPower()) return;

  const elements = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-garden-parallax]')]);
  if (!elements.length) return;

  for (const element of elements) {
    if (parallaxItems.some((item) => item.element === element)) continue;
    const speed = Number(element.dataset.gardenParallax) || 0.12;
    parallaxItems.push({ element, speed });
  }

  if (parallaxItems.length && !window.__gardenParallaxBound) {
    window.__gardenParallaxBound = true;
    // In cattura sul documento: vale per la finestra E per lo scroller
    // interno di Horizon su desktop (.page-wrapper).
    document.addEventListener('scroll', requestParallax, { capture: true, passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
  }

  requestParallax();
}

/* ------------------------------------------------------------------ *
 * Inclinazione card
 * ------------------------------------------------------------------ */

/** @param {ParentNode} root */
function initTilt(root) {
  if (!motionAllowed() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  for (const element of root.querySelectorAll('[data-garden-tilt]')) {
    if (!(element instanceof HTMLElement) || element.dataset.gardenTiltBound === 'true') continue;
    element.dataset.gardenTiltBound = 'true';

    const strength = Number(element.dataset.gardenTilt) || 6;

    element.addEventListener('pointermove', (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      element.classList.add('garden-tilting');
      element.style.setProperty('--garden-tilt-y', `${(x * strength).toFixed(2)}deg`);
      element.style.setProperty('--garden-tilt-x', `${(-y * strength).toFixed(2)}deg`);
      element.style.setProperty('--garden-tilt-lift', '-4px');
    });

    element.addEventListener('pointerleave', () => {
      element.classList.remove('garden-tilting');
      element.style.setProperty('--garden-tilt-y', '0deg');
      element.style.setProperty('--garden-tilt-x', '0deg');
      element.style.setProperty('--garden-tilt-lift', '0px');
    });
  }
}

/* ------------------------------------------------------------------ *
 * Bottoni magnetici
 * ------------------------------------------------------------------ */

/** @param {ParentNode} root */
function initMagnetic(root) {
  if (!motionAllowed() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  for (const element of root.querySelectorAll('[data-garden-magnetic]')) {
    if (!(element instanceof HTMLElement) || element.dataset.gardenMagneticBound === 'true') continue;
    element.dataset.gardenMagneticBound = 'true';

    const strength = Number(element.dataset.gardenMagnetic) || 0.28;

    element.addEventListener('pointermove', (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - (rect.left + rect.width / 2)) * strength;
      const y = (event.clientY - (rect.top + rect.height / 2)) * strength;
      element.style.setProperty('--garden-magnet-x', `${x.toFixed(1)}px`);
      element.style.setProperty('--garden-magnet-y', `${y.toFixed(1)}px`);
    });

    element.addEventListener('pointerleave', () => {
      element.style.setProperty('--garden-magnet-x', '0px');
      element.style.setProperty('--garden-magnet-y', '0px');
    });
  }
}

/* ------------------------------------------------------------------ *
 * Contatori
 * ------------------------------------------------------------------ */

/** @param {ParentNode} root */
function initCounters(root) {
  const counters = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-garden-count]')]);
  if (!counters.length) return;

  const run = (/** @type {HTMLElement} */ element) => {
    const raw = element.dataset.gardenCount ?? '';
    const target = Number(raw);
    const suffix = element.dataset.gardenCountSuffix || '';

    // Valore non numerico (es. "2.400"): lo mostriamo cosi' com'e', senza conteggio.
    if (!Number.isFinite(target)) {
      element.textContent = `${raw}${suffix}`;
      return;
    }

    if (!motionAllowed()) {
      element.textContent = `${target}${suffix}`;
      return;
    }

    const duration = 1400;
    const startedAt = performance.now();

    const step = (/** @type {number} */ now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      element.textContent = `${Math.round(target * eased)}${suffix}`;
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  // Stessa logica delle rivelazioni: un contatore saltato da uno scroll rapido
  // resterebbe fermo a zero con IntersectionObserver.
  for (const counter of counters) {
    if (counter.dataset.gardenCountPending === 'true' || counter.dataset.gardenCountDone === 'true') continue;
    counter.dataset.gardenCountPending = 'true';
    contatoriInAttesa.set(counter, run);
  }

  requestReveals();
}

/* ------------------------------------------------------------------ *
 * Particelle ambientali
 * ------------------------------------------------------------------ */

/** @param {ParentNode} root */
function initParticles(root) {
  if (!motionAllowed() || isLowPower()) return;

  for (const layer of root.querySelectorAll('.garden-particles')) {
    if (!(layer instanceof HTMLElement) || layer.dataset.gardenParticlesBuilt === 'true') continue;
    if (layer.dataset.kind === 'none' || !layer.dataset.kind) continue;

    layer.dataset.gardenParticlesBuilt = 'true';

    const requested = Number(layer.dataset.count) || 18;
    const count = Math.min(requested, window.innerWidth < 750 ? 12 : 32);
    const falling = layer.dataset.kind !== 'fireflies';
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < count; index += 1) {
      const particle = document.createElement('span');
      particle.className = 'garden-particle';
      particle.setAttribute('aria-hidden', 'true');

      let size = falling ? 6 + Math.random() * 10 : 3 + Math.random() * 6;
      if (layer.dataset.kind === 'flowers') size = 12 + Math.random() * 12;
      particle.style.setProperty('--garden-particle-size', `${size.toFixed(1)}px`);
      particle.style.setProperty('--garden-particle-left', `${(Math.random() * 100).toFixed(2)}%`);
      particle.style.setProperty('--garden-particle-top', falling ? '-6%' : `${(15 + Math.random() * 75).toFixed(2)}%`);
      particle.style.setProperty('--garden-particle-x', `${(Math.random() * 160 - 80).toFixed(0)}px`);
      particle.style.setProperty('--garden-particle-y', `${(-60 - Math.random() * 120).toFixed(0)}px`);
      particle.style.setProperty('--garden-particle-duration', `${(falling ? 12 : 8) + Math.random() * 10}s`);
      particle.style.setProperty('--garden-particle-delay', `${(Math.random() * 12).toFixed(2)}s`);

      fragment.appendChild(particle);
    }

    layer.appendChild(fragment);
  }
}

/* ------------------------------------------------------------------ *
 * Avvio
 * ------------------------------------------------------------------ */

/** @param {ParentNode} [root] */
export function initGardenMotion(root = document) {
  initReveals(root);
  initParallax(root);
  initTilt(root);
  initMagnetic(root);
  initCounters(root);
  initParticles(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initGardenMotion(), { once: true });
} else {
  initGardenMotion();
}

// Re-inizializza quando l'editor tema ricarica una sezione.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {CustomEvent} */ (event).target;
  if (target instanceof HTMLElement) initGardenMotion(target);
});

// Il ripristino da bfcache non ri-esegue il modulo: rimettiamo in moto la parallasse.
window.addEventListener('pageshow', (event) => {
  if (/** @type {PageTransitionEvent} */ (event).persisted) requestParallax();
});

REDUCED_MOTION.addEventListener('change', () => {
  if (!motionAllowed()) {
    document.querySelectorAll('[data-garden-reveal]').forEach((element) => element.classList.add('garden-revealed'));
  }
});

/* v2 — fix scroller desktop (.page-wrapper) */
